import React, { useState, useEffect, useContext } from "react";
import {
  makeStyles,
  Paper,
  Grid,
  TextField,
  Button,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Box,
  IconButton,
  Divider
} from "@material-ui/core";
import {
  Phone as PhoneIcon,
  CallEnd as CallEndIcon,
  Refresh as RefreshIcon
} from "@material-ui/icons";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  dialerCard: {
    marginBottom: theme.spacing(3),
  },
  phoneInput: {
    marginBottom: theme.spacing(2),
  },
  callButton: {
    height: 56,
    fontSize: 18,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
      transform: 'translateY(-2px)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
    '& .MuiSvgIcon-root': {
      fontSize: '1.5rem',
      transition: 'transform 0.3s ease',
    },
    '&:hover .MuiSvgIcon-root': {
      transform: 'scale(1.1) rotate(15deg)',
    },
  },
  statusCard: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
  },
  transcriptBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: 4,
    maxHeight: 400,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  transcriptMessage: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: 4,
  },
  userMessage: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  },
  aiMessage: {
    backgroundColor: theme.palette.grey[200],
  },
  configSection: {
    marginTop: theme.spacing(3),
  },
}));

const countryCodes = [
  { code: "+1", country: "🇺🇸 USA/Canada", flag: "🇺🇸" },
  { code: "+52", country: "🇲🇽 México", flag: "🇲🇽" },
  { code: "+55", country: "🇧🇷 Brasil", flag: "🇧🇷" },
  { code: "+34", country: "🇪🇸 España", flag: "🇪🇸" },
  { code: "+54", country: "🇦🇷 Argentina", flag: "🇦🇷" },
  { code: "+57", country: "🇨🇴 Colombia", flag: "🇨🇴" },
  { code: "+56", country: "🇨🇱 Chile", flag: "🇨🇱" },
  { code: "+51", country: "🇵🇪 Perú", flag: "🇵🇪" },
  { code: "+58", country: "🇻🇪 Venezuela", flag: "🇻🇪" },
  { code: "+593", country: "🇪🇨 Ecuador", flag: "🇪🇨" },
];

const AICall = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [countryCode, setCountryCode] = useState("+52");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [voiceId, setVoiceId] = useState("");
  const [model, setModel] = useState("gpt-4");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [language, setLanguage] = useState("es");
  
  const [calling, setCalling] = useState(false);
  const [callStatus, setCallStatus] = useState("");
  const [currentCallSid, setCurrentCallSid] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [queuePosition, setQueuePosition] = useState(null);

  useEffect(() => {
    // Set default system prompt
    setSystemPrompt(
      "Eres un asistente de IA amable y servicial. Responde preguntas de manera clara y concisa. Mantén las respuestas breves y naturales para conversación por voz."
    );
  }, []);

  const handleInitiateCall = async () => {
    if (!phoneNumber) {
      toast.error("Por favor ingresa un número de teléfono");
      return;
    }

    const fullPhoneNumber = `${countryCode}${phoneNumber}`;

    try {
      setCalling(true);
      setCallStatus("Iniciando llamada...");
      setTranscript([]);

      const { data } = await api.post("/api/twilio/calls", {
        phoneNumber: fullPhoneNumber,
        companyId: user.companyId,
        voiceId: voiceId || undefined,
        model,
        systemPrompt,
        language
      });

      setQueuePosition(data.position);
      setCallStatus(`En cola - Posición: ${data.position}`);
      
      toast.success("Llamada iniciada exitosamente");

      // Poll for call status
      // Note: In production, use WebSocket for real-time updates
      setTimeout(() => {
        setCallStatus("Marcando...");
      }, 2000);

    } catch (error) {
      console.error("Error initiating call:", error);
      toast.error(error.response?.data?.error || "Error al iniciar la llamada");
      setCalling(false);
      setCallStatus("");
    }
  };

  const handleEndCall = async () => {
    if (!currentCallSid) {
      setCalling(false);
      setCallStatus("");
      return;
    }

    try {
      await api.post(`/api/twilio/calls/${currentCallSid}/terminate`);
      toast.success("Llamada finalizada");
      setCalling(false);
      setCallStatus("");
      setCurrentCallSid(null);
    } catch (error) {
      console.error("Error ending call:", error);
      toast.error("Error al finalizar la llamada");
    }
  };

  const formatPhoneNumber = (value) => {
    // Remove non-digits
    const cleaned = value.replace(/\D/g, "");
    return cleaned;
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Llamar con IA</Title>
        <MainHeaderButtonsWrapper>
          <IconButton onClick={() => window.location.reload()}>
            <RefreshIcon />
          </IconButton>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Grid container spacing={3}>
          {/* Dialer Section */}
          <Grid item xs={12} md={6}>
            <Card className={classes.dialerCard}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Marcar Número
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>País</InputLabel>
                      <Select
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                        label="País"
                        disabled={calling}
                      >
                        {countryCodes.map((country) => (
                          <MenuItem key={country.code} value={country.code}>
                            {country.country}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={8}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Número de Teléfono"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
                      placeholder="1234567890"
                      disabled={calling}
                      className={classes.phoneInput}
                    />
                  </Grid>
                </Grid>

                <Box mt={2}>
                  {!calling ? (
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      size="large"
                      startIcon={<PhoneIcon />}
                      onClick={handleInitiateCall}
                      className={classes.callButton}
                    >
                      Llamar
                    </Button>
                  ) : (
                    <Button
                      fullWidth
                      variant="contained"
                      color="secondary"
                      size="large"
                      startIcon={<CallEndIcon />}
                      onClick={handleEndCall}
                      className={classes.callButton}
                    >
                      Finalizar Llamada
                    </Button>
                  )}
                </Box>

                {callStatus && (
                  <Box mt={2} textAlign="center">
                    <Chip
                      label={callStatus}
                      color={calling ? "primary" : "default"}
                      icon={calling ? <CircularProgress size={16} /> : undefined}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Configuration Section */}
            <Card className={classes.configSection}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Configuración de IA
                </Typography>

                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel>Modelo</InputLabel>
                  <Select
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    label="Modelo"
                    disabled={calling}
                  >
                    <MenuItem value="gpt-4">GPT-4</MenuItem>
                    <MenuItem value="gpt-4-turbo">GPT-4 Turbo</MenuItem>
                    <MenuItem value="gpt-3.5-turbo">GPT-3.5 Turbo</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel>Idioma</InputLabel>
                  <Select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    label="Idioma"
                    disabled={calling}
                  >
                    <MenuItem value="es">Español</MenuItem>
                    <MenuItem value="en">English</MenuItem>
                    <MenuItem value="pt">Português</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  variant="outlined"
                  label="Voice ID (ElevenLabs)"
                  value={voiceId}
                  onChange={(e) => setVoiceId(e.target.value)}
                  placeholder="Dejar vacío para usar voz por defecto"
                  disabled={calling}
                  margin="normal"
                />

                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  label="Prompt del Sistema"
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  disabled={calling}
                  margin="normal"
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Transcript Section */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Transcripción en Tiempo Real
                </Typography>
                <Divider />
                
                <Box className={classes.transcriptBox}>
                  {transcript.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" align="center">
                      La transcripción aparecerá aquí durante la llamada
                    </Typography>
                  ) : (
                    transcript.map((message, index) => (
                      <Box
                        key={index}
                        className={`${classes.transcriptMessage} ${
                          message.role === "user"
                            ? classes.userMessage
                            : classes.aiMessage
                        }`}
                      >
                        <Typography variant="caption" display="block">
                          <strong>{message.role === "user" ? "Usuario" : "IA"}:</strong>
                        </Typography>
                        <Typography variant="body2">{message.text}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {message.timestamp}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    </MainContainer>
  );
};

export default AICall;
